import { useEffect, useRef, useState } from 'react'
import { portalLinks } from './portal-links'
import './web-test-infra.css'
import './web-test-portal.css'

const FLOW = ['Perfect Predict', 'Client Portal', 'MetaTrader 5', 'Market'] as const

const PORTAL_FEATURES = [
  'Trading Accounts',
  'Trading History',
  'Deposits',
  'Withdrawals',
  'Transactions',
  'Support',
] as const

export function WebTestInfrastructure() {
  const rootRef = useRef<HTMLElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true)
          io.disconnect()
        }
      },
      { threshold: 0.1 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <section
      ref={rootRef}
      id="infrastructure"
      className={`wi-section wi-section--merged${visible ? ' is-visible' : ''}`}
      aria-labelledby="wi-title"
    >
      {/* Deep-link landmark retained for existing #client-portal anchors */}
      <div id="client-portal" />
      <div className="wi-inner">
        <div className="wi-merged">
          <div className="wi-merged__left pp-enter" style={{ ['--pp-i' as string]: 1 }}>
            <header className="wi-intro">
              <p className="wi-eyebrow">Platform</p>
              <h2 id="wi-title" className="wi-title">
                Institutional Infrastructure.
                <br />
                One Connected Experience.
              </h2>
              <p className="wi-subtitle">
                Secure account management in the Client Portal, with execution through MetaTrader 5
                infrastructure.
              </p>
            </header>

            <p className="wi-merged__lead">
              Perfect Predict connects structured prediction trading to institutional execution
              through one secure client experience.
            </p>

            <div className="wi-flow wi-flow--compact" aria-label="Execution path">
              <p className="wi-flow__label">Execution path</p>
              <ol className="wi-flow__list wi-flow__list--row">
                {FLOW.map((step, index) => (
                  <li key={step} className="wi-flow__step">
                    <span className="wi-flow__block">{step}</span>
                    {index < FLOW.length - 1 ? (
                      <span className="wi-flow__connector wi-flow__connector--h" aria-hidden="true">
                        →
                      </span>
                    ) : null}
                  </li>
                ))}
              </ol>
            </div>

            <ul className="wi-feature-list">
              {PORTAL_FEATURES.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>

            <div className="wi-merged__cta">
              <a className="wt-btn wt-btn--secondary wt-btn--hero" href={portalLinks.login}>
                Client Portal Login
              </a>
            </div>
          </div>

          <div className="wi-merged__right pp-enter" style={{ ['--pp-i' as string]: 2 }}>
            <figure className="wpo-browser wpo-browser--desktop" aria-label="Client Portal preview">
              <div className="wpo-browser__chrome" aria-hidden="true">
                <span className="wpo-browser__dots">
                  <i />
                  <i />
                  <i />
                </span>
                <span className="wpo-browser__url">portal.perfectpredict.com/app</span>
              </div>
              <img
                src="/web-test/portal-preview-desktop.png"
                alt="Perfect Predict Client Portal dashboard preview with illustrative fixture data"
                width={1440}
                height={900}
                loading="lazy"
              />
            </figure>

            <figure className="wpo-browser wpo-browser--mobile" aria-label="Client Portal mobile preview">
              <div className="wpo-phone">
                <div className="wpo-phone__notch" aria-hidden="true" />
                <img
                  src="/web-test/portal-preview-mobile.png"
                  alt="Perfect Predict Client Portal mobile dashboard preview"
                  width={390}
                  height={844}
                  loading="lazy"
                />
              </div>
            </figure>
          </div>
        </div>
      </div>
    </section>
  )
}
